import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { DatePicker, Form, Input } from 'antd';
import { useSelector } from 'react-redux';
import { selectUpdatedPlannedResourcesArr } from '../../slice/selectors';
import {
  ICreateTask,
  ICreateTaskPayload,
  ITask,
  ITaskFormValues,
  ITaskPlannedResource,
  taskFormValuesEnum,
} from '../../slice/types';
import { CREATE_TASK_FORM_ID } from '../../pages/CreateOrUpdateTask/CreateOrUpdateTask';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { selectSelectedPolygonIds } from 'app/containers/Map/slice/selectors';

import TaskCategorySelect from 'app/components/Select/TaskCategorySelect/TaskCategorySelect';
import { showNoSelectedFieldsErrorNotification } from 'utils/notifications';

interface IProps {
  initialTask?: ITask;
  isEditPage: boolean;
  onSuccessRedirectTo?: string;
  taskRecipeDescriptionAsNote: string | null;
  setTaskRecipeDescriptionAsNote: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  onSubmit: ({ payload }: { payload: ICreateTaskPayload }) => void;
}

const CreateOrUpdateTaskForm: FC<IProps> = ({
  onSubmit,
  initialTask,
  isEditPage,
  onSuccessRedirectTo,
  taskRecipeDescriptionAsNote,
  setTaskRecipeDescriptionAsNote,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const selectedEnvId = useSelector(selectSelectedEnvId) as number;
  const updatedPlannedResourcesArr: ITaskPlannedResource[] = useSelector(
    selectUpdatedPlannedResourcesArr,
  );
  const selectedPolygonIds: number[] = useSelector(selectSelectedPolygonIds);

  useEffect(() => {
    if (taskRecipeDescriptionAsNote?.length) {
      form.setFieldsValue({
        [taskFormValuesEnum.notes]: taskRecipeDescriptionAsNote,
      });
      setTaskRecipeDescriptionAsNote(null);
    }
  }, [taskRecipeDescriptionAsNote, setTaskRecipeDescriptionAsNote, form]);

  useEffect(() => {
    if (isEditPage && initialTask) {
      form.setFieldsValue({
        [taskFormValuesEnum.notes]: initialTask.note,
        [taskFormValuesEnum.name]: initialTask.name,
        [taskFormValuesEnum.category]: initialTask.category.id,
        [taskFormValuesEnum.date]: [
          moment(initialTask.startDate),
          moment(initialTask.endDate),
        ],
      });
    }
  }, [isEditPage, initialTask, form]);

  const onFinish = (formValues: ITaskFormValues) => {
    if (!selectedPolygonIds.length) {
      showNoSelectedFieldsErrorNotification();
      return;
    }
    const { date, taskCategoryId, name, notes } = formValues;

    const data: ICreateTask = {
      startDate: moment(date[0]).set('hour', 12).utc().format(),
      endDate: moment(date[1]).set('hour', 12).utc().format(),
      taskCategoryId: Number(taskCategoryId),
      fieldSeasons: selectedPolygonIds,
      hiikoEnvironmentId: selectedEnvId,
      name,
      note: notes?.trim() || '',
    };

    const payload: ICreateTaskPayload = {
      task: data,
      envId: selectedEnvId,
      onSuccessRedirectTo,
      plannedResources: updatedPlannedResourcesArr,
    };

    onSubmit({ payload });
  };

  return (
    <Form
      layout="vertical"
      form={form}
      initialValues={{ layout: 'vertical' }}
      requiredMark={false}
      id={CREATE_TASK_FORM_ID}
      onFinish={onFinish}
    >
      <Form.Item
        label={t('Task.CreateTask.TaskName', 'Task name')}
        name={taskFormValuesEnum.name}
        rules={[{ required: true }]}
      >
        <Input placeholder={t('Task.CreateTask.TaskName', 'Task name')} />
      </Form.Item>

      <Form.Item
        label={t('Task.CreateTask.TaskCategory', 'Task category')}
        name={taskFormValuesEnum.category}
        rules={[{ required: true }]}
      >
        <TaskCategorySelect
          placeholder={t('Task.CreateTask.TaskCategory', 'Task category')}
          getPopupContainer={trigger => trigger.parentNode}
        />
      </Form.Item>

      <Form.Item
        label={t('Task.CreateTask.CompleteTheTask', 'Complete the task')}
        name={taskFormValuesEnum.date}
        rules={[{ required: true }]}
      >
        <DatePicker.RangePicker style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item
        label={t('Task.CreateTask.Notes', 'Notes')}
        name={taskFormValuesEnum.notes}
      >
        <Input.TextArea
          rows={4}
          placeholder={t('Task.CreateTask.Notes', 'Notes')}
        />
      </Form.Item>
    </Form>
  );
};

export default CreateOrUpdateTaskForm;
